import {
  GewijzigdeInzetSignaal,
  InregelingSignaal,
  InzetbaarheidSignaal,
  NieuweBewegingSignaal,
  NieuweClaimSignaal,
  OmleidingSignaal,
  OpheffenSignaal,
  Signaal,
  SignaalBetrokkenWerklijn,
  SignaalClaim,
  SignaalInzet,
  SignaalInzetOfClaim,
  VerlengenSignaal,
  VersterkenSignaal,
  VerwijderdeClaimSignaal,
  VerzwakSignaal,
  WisselenSignaal,
} from '@/types/Signalen';
import { dagEnMaandDatum, tijdstipDatum } from '@/utils/dateUtils';
import { parseInzetPositie } from '@/utils/helpers';
import { randomBase36 } from '@/utils/strings';

type InfoElementType =
  | 'eenheid'
  | 'treinnummer'
  | 'claimcode'
  | 'treindatum'
  | 'dag'
  | 'tijd'
  | 'betrokkenWerklijnen'
  | 'traject'
  | 'eersteInzet'
  | 'misc'
  | 'eenheid_misc';

export type InfoElement = {
  elemId: string;
  type: InfoElementType;
  tekst: string;
};

const elem: (tekst: string, type?: InfoElementType) => InfoElement = (tekst, type = 'misc') => {
  return { elemId: randomBase36(), tekst, type };
};

const N_DASH = '\u2013';
const MANCO = 'Manco';
const EENHEID = 'eenheid';
const EENHEID_MISC = 'eenheid_misc';
const TREINNUMMER = 'treinnummer';
const TREINDATUM = 'treindatum';
const DAG = 'dag';
const DATUMTIJD = 'tijd';
const BETROKKEN_WERKLIJNEN = 'betrokkenWerklijnen';
const TRAJECT = 'traject';
const EERSTE_INZET = 'eersteInzet';

const dagEnMaandTreindatum = (treindatum: string | null) =>
  treindatum ? `${treindatum.slice(8, 10)}-${treindatum.slice(5, 7)}` : N_DASH;

const naInzetBeschrijving = (naInzet: SignaalInzetOfClaim) =>
  naInzet ? `na ${inzetOfClaimBeschrijving(naInzet)} ${naInzet.traject.dienstregelpuntTot}` : 'als eerste inzet';

const treinnummerMetPositie = (treinnummer: number, inzetPositie: number) => {
  const parsedInzetPositie = parseInzetPositie(inzetPositie) ? `-${parseInzetPositie(inzetPositie)}` : '';
  return `${treinnummer}${parsedInzetPositie}`;
};

const omschrijvingInzetPlusPositie = (inzet: SignaalInzet) =>
  treinnummerMetPositie(inzet.bewegingSleutel.treinnummer, inzet.inzetPositie);

export const signaalInfo = (signaal: Signaal): InfoElement[] => {
  switch (signaal.type) {
    case 'GewijzigdeInzetSignaal':
      return infoVoorGewijzigdeInzetSignaal(signaal);
    case 'InregelingSignaal':
      return infoVoorInregelingSignaal(signaal);
    case 'NieuweBewegingSignaal':
      return infoVoorNieuweBewegingSignaal(signaal);
    case 'OmleidingSignaal':
      return infoVoorOmleidingSignaal(signaal);
    case 'OpheffenSignaal':
      return infoVoorOpheffenSignaal(signaal);
    case 'VersterkenSignaal':
      return infoVoorVersterkenSignaal(signaal);
    case 'NieuweClaimSignaal':
      return infoVoorNieuweClaimSignaal(signaal);
    case 'VerzwakSignaal':
      return infoVoorVerzwakSignaal(signaal);
    case 'VerwijderdeClaimSignaal':
      return infoVoorVerwijderdeClaimSignaal(signaal);
    case 'WisselenSignaal':
      return infoVoorWisselenSignaal(signaal);
    case 'InzetbaarheidSignaal':
      return infoVoorInzetbaarheidSignaal(signaal);
    case 'VerlengenSignaal':
      return infoVoorVerlengenSignaal(signaal);
    default:
      // Onbekend signaaltype: toon geen details i.p.v. de UI te laten crashen
      return [];
  }
};

function inzetOfClaimBeschrijving(inzet: SignaalInzetOfClaim) {
  if (inzet.type === 'InzetOpBeweging') {
    return omschrijvingInzetPlusPositie(inzet);
  }
  return inzet.claimsoort;
}

const gesteldheidInzet = (inzet: SignaalInzet) => {
  if (inzet.opengesteld) {
    return 'opengesteld';
  }
  return inzet.opzending ? 'opzending' : '';
};

const uitgebreideClaimOmschrijving: (claim: SignaalClaim) => string = (claim) =>
  `${claim.claimsoort} ${claim.bijzonderheidscode ?? ''} ${claim.toelichting ?? ''}`;

function infoVoorGewijzigdeInzetOpBeweging(voor: SignaalInzet, na: SignaalInzet) {
  const result: InfoElement[] = [];
  // Gewijzigde toelichting:
  // <TreinNummer> op <van-dag-maand> vanaf <van-Dienstregelpunt> was "<vorige Bijzonderheid><vorige Toelichting>" wordt "<Bijzonderheid> <Toelichting>"
  // voorbeeld:
  // 1001 op 01-04 vanaf Amf was "" wordt "ICM4 Trein rijd niet verder"
  //
  // Gewijzigde bijzonderheid: https://ns-topaas.atlassian.net/wiki/spaces/BSBM/pages/148686594/Signaal+Gewijzigde+Bijzonderheid
  // voorbeeld:
  // Opzending gewijzigd 5002 op 01-04 vanaf Gd met tractie en afgesloten voor reizigers
  //
  if (voor.toelichting !== na.toelichting) {
    result.push(elem(`"${na.toelichting ?? N_DASH}" (eerder: "${voor.toelichting ?? ''}")`));
  }
  if (voor.opengesteld !== na.opengesteld || voor.opzending !== na.opzending) {
    result.push(elem(`${gesteldheidInzet(na)} (eerder: ${gesteldheidInzet(voor)})`));
  }
  if (voor.bijzonderheidscode !== na.bijzonderheidscode) {
    result.push(elem(`${na.bijzonderheidscode ?? N_DASH} (eerder: ${voor.bijzonderheidscode ?? N_DASH})`));
  }
  if (voor.tractie !== na.tractie) {
    result.push(
      elem(`${na.tractie ? 'tractie' : 'geen tractie'} (eerder: ${voor.tractie ? 'tractie' : 'geen tractie'})`),
    );
  }
  return result;
}

function infoVoorGewijzigdeClaim(voor: SignaalClaim, na: SignaalClaim) {
  const result: InfoElement[] = [];
  const beginDatum = new Date(na.beginDatumTijd);
  const eindDatum = new Date(na.eindDatumTijd);

  if (na.traject.dienstregelpuntVan === na.traject.dienstregelpuntTot) {
    // Matclaim:
    // van 19-01 14:30 tot 17:00 op Amf "BTD SD Frontsein" als eerste inzet
    // van 19-01 18:00 tot 20:00 op Ut "MWBX 405860 Oxaal wassen" als eerste inzet
    result.push(elem(dagEnMaandDatum(beginDatum)));
    result.push(elem(tijdstipDatum(beginDatum)));
    result.push(elem(tijdstipDatum(eindDatum)));
    result.push(elem(na.traject.dienstregelpuntVan));
  } else {
    // Ritclaim:
    // van Ut 19-01 14:30 naar Zl 17:00 "AFVOER 89106 Naar werkplaats”
    result.push(elem(na.traject.dienstregelpuntVan));
    result.push(elem(dagEnMaandDatum(beginDatum)));
    result.push(elem(tijdstipDatum(beginDatum)));
    result.push(elem(na.traject.dienstregelpuntTot));
    result.push(elem(tijdstipDatum(eindDatum)));
  }
  if (voor.claimsoort !== na.claimsoort) {
    result.push(elem(`${na.claimsoort} (eerder: ${voor.claimsoort}})`));
  } else {
    result.push(elem(na.claimsoort));
  }
  if (voor.bijzonderheidscode !== na.bijzonderheidscode) {
    result.push(elem(`${na.bijzonderheidscode ?? N_DASH} (eerder: ${voor.bijzonderheidscode ?? N_DASH})`));
  } else if (na.bijzonderheidscode) {
    result.push(elem(na.bijzonderheidscode));
  }
  if (voor.toelichting !== na.toelichting) {
    result.push(elem(`"${na.toelichting ?? N_DASH}" (eerder: "${voor.toelichting ?? ''}")`));
  }
  return result;
}

function infoVoorGewijzigdeInzet(voor: SignaalInzetOfClaim, na: SignaalInzetOfClaim) {
  if (voor.type === 'InzetOpBeweging' && na.type === 'InzetOpBeweging') {
    return infoVoorGewijzigdeInzetOpBeweging(voor, na);
  }
  if (voor.type === 'Claim' && na.type === 'Claim') {
    return infoVoorGewijzigdeClaim(voor, na);
  }
  console.error('voor en na moeten qua type overeenkomen!');
  return [];
}

function infoEenheidBetrokkenWerklijn(betrokkenWerklijn: SignaalBetrokkenWerklijn) {
  return betrokkenWerklijn.eenheidNummer ?? MANCO;
}

function infoEenheidBetrokkenWerklijnElem(betrokkenWerklijn: SignaalBetrokkenWerklijn) {
  return elem(infoEenheidBetrokkenWerklijn(betrokkenWerklijn), EENHEID);
}

// Gewijzigde matclaim:
// 4023 van 19-01 14:30 tot 17:00 op Amf "BTD SD Frontsein" als eerste inzet
// 4023 van 19-01 18:00 tot 20:00 op Ut "MWBX 405860 Oxaal wassen" als eerste inzet
//
function infoVoorGewijzigdeInzetSignaal(signaal: GewijzigdeInzetSignaal) {
  return [
    infoEenheidBetrokkenWerklijnElem(signaal.betrokkenWerklijn),
    ...infoVoorGewijzigdeInzet(signaal.inzetVoorMutatie, signaal.inzetNaMutatie),
  ];
}

function infoEenheidInregelingSignaal(signaal: InregelingSignaal) {
  return signaal.eenheidNummer ? `${signaal.eenheidNummer}` : '';
}

function infoVoorInregelingSignaal(signaal: InregelingSignaal) {
  const eenheidNummer = elem(infoEenheidInregelingSignaal(signaal), EENHEID);
  const result: InfoElement[] = [eenheidNummer];

  if (signaal.inregelDatumVoorMutatie) {
    if (signaal.inregelDatumNaMutatie) {
      result.push(
        elem(
          `${dagEnMaandTreindatum(signaal.inregelDatumNaMutatie)} (was ${dagEnMaandTreindatum(signaal.inregelDatumVoorMutatie)})`,
        ),
      );
    } else {
      result.push(elem(`- (was ${dagEnMaandTreindatum(signaal.inregelDatumVoorMutatie)})`));
    }
  } else if (signaal.inregelDatumNaMutatie) {
    result.push(elem(dagEnMaandTreindatum(signaal.inregelDatumNaMutatie)));
  } else {
    result.push(elem('-'));
  }
  return result;
}

// 4023 verwijderd op 19-01 "AFVOER 89001 Mat bestemd voor 814-V"
//
function infoVoorVerwijderdeClaimSignaal(signaal: VerwijderdeClaimSignaal) {
  const eenheidNummer = infoEenheidBetrokkenWerklijnElem(signaal.betrokkenWerklijn);
  const beginDag = elem(dagEnMaandDatum(new Date(signaal.verwijderdeClaim.beginDatumTijd)), DAG);
  const claimOmschrijving = elem(uitgebreideClaimOmschrijving(signaal.verwijderdeClaim));
  return [eenheidNummer, beginDag, claimOmschrijving];
}

// 4023 na 525 Amf > 687
//
function infoVoorWisselenSignaal(signaal: WisselenSignaal) {
  const eenheidNummer = infoEenheidBetrokkenWerklijnElem(signaal.betrokkenWerklijn);
  const naInzet = elem(signaal.naInzet ? naInzetBeschrijving(signaal.naInzet) : 'als eerste inzet');
  const eersteInzet = elem(
    signaal.eersteNieuweInzet
      ? `${inzetOfClaimBeschrijving(signaal.eersteNieuweInzet)} ${signaal.eersteNieuweInzet.traject.dienstregelpuntVan}`
      : 'geen inzet',
    EERSTE_INZET,
  );
  return [eenheidNummer, naInzet, eersteInzet];
}

function infoVoorNieuweBewegingSignaal(signaal: NieuweBewegingSignaal) {
  const treinnummer = elem(`${signaal.bewegingSleutel.treinnummer}`, TREINNUMMER);
  const treindatum = elem(dagEnMaandTreindatum(signaal.bewegingSleutel.treindatum), TREINDATUM);
  const traject = elem(`${signaal.traject.dienstregelpuntVan} - ${signaal.traject.dienstregelpuntTot}`, TRAJECT);
  return [treinnummer, treindatum, traject];
}

function infoVoorOmleidingSignaal(signaal: OmleidingSignaal) {
  const treinnummer = elem(`${signaal.bewegingSleutel.treinnummer}`, TREINNUMMER);
  const treindatum = elem(dagEnMaandTreindatum(signaal.bewegingSleutel.treindatum), TREINDATUM);
  const traject = elem(
    `${signaal.omgeleidTraject.dienstregelpuntVan} - ${signaal.omgeleidTraject.dienstregelpuntTot}`,
    TRAJECT,
  );
  const result = [treinnummer, treindatum, traject];
  return infoVoorBetrokkenWerklijnen(result, signaal);
}

function infoVoorBetrokkenWerklijnen(result, signaal) {
  if (signaal.betrokkenWerklijnen?.length !== 0) {
    // Sorteer volgens inregelDatum, dat die naast de icoon staan
    result.push(
      elem(
        signaal.betrokkenWerklijnen
          .sort((a, __b) => (a.inregelDatum ? -1 : 1))
          .map((bw) => infoEenheidBetrokkenWerklijn(bw))
          .join(', '),
        BETROKKEN_WERKLIJNEN,
      ),
    );
  }
  return result;
}

function infoVoorOpheffenSignaal(signaal: OpheffenSignaal) {
  const treinnummer = elem(`${signaal.bewegingSleutel.treinnummer}`, TREINNUMMER);
  const treindatum = elem(dagEnMaandTreindatum(signaal.bewegingSleutel.treindatum), TREINDATUM);
  const traject = elem(
    `${signaal.opgehevenTraject.dienstregelpuntVan} - ${signaal.opgehevenTraject.dienstregelpuntTot}`,
    TRAJECT,
  );
  const result = [treinnummer, treindatum, traject];
  return infoVoorBetrokkenWerklijnen(result, signaal);
}

// Materieelclaim 4023 van 19-01 14:30 tot 17:00 op Amf "MWBX 405860 Oxaal wassen" als eerste inzet
// Materieelclaim 4023 van 19-01 14:30 tot 17:00 op Amf "MWBX 405860 Oxaal wassen" na 525 Amf
// Ritclaim       4023 van Rtd 19-01 07:00 naar Ut 12:00 "AFVOER 89001 Mat bestemd voor 814-V" als eerste inzet
// Ritclaim       4023 van Amf 19-01 14:30 naar Ut 17:00 "AFVOER 89001 Mat bestemd voor 814-V" na 525 Amf
//
//                4023 | Rtd | 19-01 | 07:00 | Ut
function infoVoorNieuweClaimSignaal(signaal: NieuweClaimSignaal) {
  const { traject } = signaal.claim;
  const beginDate = new Date(signaal.claim.beginDatumTijd);
  const eindDate = new Date(signaal.claim.eindDatumTijd);
  const eenheidNummer = infoEenheidBetrokkenWerklijnElem(signaal.betrokkenWerklijn);
  const beginTraject = elem(`${dagEnMaandDatum(beginDate)} ${tijdstipDatum(beginDate)} ${traject.dienstregelpuntVan}`);
  const result = [eenheidNummer, beginTraject];

  if (traject.dienstregelpuntVan !== traject.dienstregelpuntTot) {
    let eindTraject = `${tijdstipDatum(eindDate)} ${signaal.claim.traject.dienstregelpuntTot}`;
    if (signaal.naInzet) {
      eindTraject = `${eindTraject} ${naInzetBeschrijving(signaal.naInzet)}`;
    }
    result.push(elem(eindTraject));
  }
  return result;
}

// 525 versterkt van 18-02 Ut tot Amf met 4024
// 525 versterkt van 18-02 Ut tot Amf met manco
//
function infoVoorVersterkenSignaal(signaal: VersterkenSignaal) {
  const treinnummer = elem(`${signaal.bewegingSleutel.treinnummer}`, TREINNUMMER);
  const treindatum = elem(dagEnMaandTreindatum(signaal.bewegingSleutel.treindatum), TREINDATUM);
  const traject = elem(
    `${signaal.versterktTraject.dienstregelpuntVan} - ${signaal.versterktTraject.dienstregelpuntTot}`,
    TRAJECT,
  );
  const eenheidNummer = elem(`met ${signaal.versterkteWerklijn.eenheidNummer ?? MANCO}`, EENHEID_MISC);
  return [treinnummer, treindatum, traject, eenheidNummer];
}

// 525-A verzwakt van 18-02 Ut tot Zl was 4024
// 525-A verzwakt van 18-02 Ut tot Amf was manco
//
function infoVoorVerzwakSignaal(signaal: VerzwakSignaal) {
  const treinMetPos = elem(
    treinnummerMetPositie(signaal.bewegingSleutel.treinnummer, signaal.inzetPositie),
    TREINNUMMER,
  );
  const treindatum = elem(dagEnMaandTreindatum(signaal.bewegingSleutel.treindatum), TREINDATUM);
  const traject = elem(
    `${signaal.verzwaktTraject.dienstregelpuntVan} - ${signaal.verzwaktTraject.dienstregelpuntTot}`,
    TRAJECT,
  );
  const eenheidNummer = elem(`was ${signaal.verzwakteWerklijn.eenheidNummer ?? MANCO}`, EENHEID_MISC);
  return [treinMetPos, treindatum, traject, eenheidNummer];
}
// 2401 | volledig inzetbaar [datum/tijd]
// 2401 | beperkt inzetbaar vanaf [datum/tijd] - reden
// 2401 | niet inzetbaar vanaf [datum/tijd] - reden
function infoVoorInzetbaarheidSignaal(signaal: InzetbaarheidSignaal) {
  const stel = elem(signaal.eenheidNummer, EENHEID);
  const eenheidStatus = elem(statusTranslation[signaal.eenheidStatus ?? ''] ?? '');
  const datumTijd = elem(`${dagEnMaandDatum(signaal.ingangsdatum)} ${tijdstipDatum(signaal.ingangsdatum)}`, DATUMTIJD);
  const reden = elem(signaal.eenheidStatus !== 'VOLLEDIG_INZETBAAR' ? (signaal.reden ?? '') : '');
  return [stel, eenheidStatus, datumTijd, reden];
}

const statusTranslation: { [status in string]: string } = {
  NIET_INZETBAAR: 'Niet inzetbaar',
  BEPERKING_IN_TREINDIENST: 'Beperking in treindienst',
  GEEN_BEPERKINGEN: 'Geen beperkingen',
  VOLLEDIG_INZETBAAR: 'Volledig inzetbaar',
  VERWACHT_GEREED: 'Verwacht gereed',
};

function infoVoorVerlengenSignaal(signaal: VerlengenSignaal) {
  const treinnummer = elem(`${signaal.bewegingSleutel.treinnummer}`, TREINNUMMER);
  const treindatum = elem(dagEnMaandTreindatum(signaal.bewegingSleutel.treindatum), TREINDATUM);
  const traject = elem(
    `${signaal.verlengdTraject.dienstregelpuntVan} - ${signaal.verlengdTraject.dienstregelpuntTot}`,
    TRAJECT,
  );
  return [treinnummer, treindatum, traject];
}
